import { z } from "zod";
import { actionSchema, focusEffectSchema } from "./action.js";
import { limits } from "./limits.js";
import { meshLayouts } from "./palette.js";
import { assetKeySchema, colorSchema } from "./primitives.js";
import { cardElementSchema, cardPresetRefSchema, withCardElements, withElementMotion } from "./card.js";

/**
 * The shapes a patterned background can take.
 *
 * Every one is drawn from exactly two colours, so the whole set re-tints at
 * once and a project keeps its palette whichever it picks. Thirteen genuinely
 * different shapes rather than one shape at a dozen angles — the colours are
 * what make a background yours, and a long grid of near-duplicates is a scroll
 * that answers one question.
 */
export const backgroundPatterns = [
  "solid",
  "linear",
  "glow",
  "corner",
  "base",
  "panel",
  "band",
  "arc",
  "swell",
  "curve",
  "hatch",
  "dots",
  "grid",
] as const;
export type BackgroundPattern = (typeof backgroundPatterns)[number];

/** How each reads in the picker. */
export const backgroundPatternLabels: Record<BackgroundPattern, string> = {
  solid: "Solid",
  linear: "Linear",
  glow: "Glow",
  corner: "Corner",
  base: "Base",
  panel: "Panel",
  band: "Band",
  arc: "Arc",
  swell: "Swell",
  curve: "Curve",
  hatch: "Hatch",
  dots: "Dots",
  grid: "Grid",
};

export const backgroundSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("color"), color: colorSchema }),
  z.object({
    kind: z.literal("gradient"),
    from: colorSchema,
    to: colorSchema,
    angle: z.number().min(0).max(360).default(135),
  }),
  /**
   * A drawn background. Two colours and a shape, nothing else — see
   * `backgroundPatterns` for why.
   */
  z.object({
    kind: z.literal("pattern"),
    pattern: z.enum(backgroundPatterns).default("linear"),
    from: colorSchema,
    to: colorSchema,
  }),
  /**
   * Several soft colour fields over a ground.
   *
   * Its own arm rather than a fourteenth pattern, because a pattern is a ground
   * and a shape in one other colour and a two-colour mesh is indistinguishable
   * from `glow`. The fields are a list rather than a fixed pair for the same
   * reason: three or four colours bleeding into each other is the whole thing,
   * and three of the palettes only carry two fields to give.
   */
  z.object({
    kind: z.literal("mesh"),
    layout: z.enum(meshLayouts).default("drift"),
    ground: colorSchema,
    fields: z.array(colorSchema).min(1).max(4),
  }),
  z.object({ kind: z.literal("image"), assetKey: assetKeySchema, dim: z.number().min(0).max(1).default(0.35) }),
]);

/**
 * Two colours to draw a pattern from, given one.
 *
 * A pattern is a ground and a shape on it, so it needs two colours that can be
 * told apart. Switching a solid backdrop to a pattern used to carry the single
 * colour into both ends, which drew every one of the thirteen patterns as the
 * same flat rectangle — a picker that looks broken rather than a set of shapes.
 *
 * The chosen colour stays as the ground, because it is the one that was
 * actually picked; the partner is derived from it so the palette is still
 * recognisably the same. Lifted when the ground is dark and dropped when it is
 * light, since a shadow on near-black and a highlight on near-white are both
 * invisible. A colour that cannot be read — an `rgba()`, a named colour — falls
 * back to a pair that is at least legible.
 */
export function backdropPair(colour: string): { from: string; to: string } {
  const hex = /^#([0-9a-f]{6})$/i.exec(colour.trim());
  if (!hex) return { from: "#eef1ff", to: "#2563eb" };

  const value = parseInt(hex[1], 16);
  const channels = [(value >> 16) & 255, (value >> 8) & 255, value & 255];
  const light = (0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2]) / 255;

  // Far enough to read as a different colour, near enough to read as the same
  // palette. Toward white for a dark ground, toward black for a light one.
  const shift = 0.42;
  const partner = channels
    .map((channel) => (light < 0.5 ? channel + (255 - channel) * shift : channel * (1 - shift)))
    .map((channel) => Math.round(Math.min(255, Math.max(0, channel))))
    .map((channel) => channel.toString(16).padStart(2, "0"))
    .join("");

  return { from: `#${partner}`, to: colour.trim().toLowerCase() };
}

export const defaultBackground = { kind: "color", color: "#0b1020" } as const;

/**
 * How the recording sits on its background.
 *
 * A screen recording played edge to edge reads as a raw capture. Inset it a
 * little, round the corners and let it cast a shadow and the same footage reads
 * as a product shot — the background stops being dead space behind the video
 * and becomes the thing the video is presented on.
 *
 * Off by default, and off means pixel-for-pixel what the renderer did before:
 * every existing project keeps the framing its owner already approved.
 *
 * `padding` is a fraction of the composition's width rather than a pixel count
 * so the inset is the same proportion at any output size. `radius` is in
 * composition pixels, which is a fixed 1920-wide space, so it does not need the
 * same treatment and reads more naturally as a number.
 */
export const frameSchema = z.object({
  enabled: z.boolean().default(false),
  padding: z.number().min(0).max(0.2).default(0.05),
  radius: z.number().min(0).max(80).default(20),
  /** A preset rather than free CSS: three depths that were drawn to look right. */
  shadow: z.enum(["none", "soft", "strong"]).default("soft"),
});

export const defaultFrame = { enabled: false, padding: 0.05, radius: 20, shadow: "soft" } as const;

/**
 * A HEAD or END scene renders one of four content kinds.
 *
 * Wrapped in a preprocess because a card is no longer a title and a subtitle:
 * `withCardElements` turns the two stored lines into two placed elements on the
 * way in, so nothing has to be rewritten in the database before it renders and
 * a card nobody opens is never touched.
 */
export const bookendContentSchema = z.preprocess(
  withCardElements,
  z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("card"),
      /**
       * Everything drawn on the card, in paint order. Replaces `title` and
       * `subtitle`, which are read into it and then gone: two sources for the
       * same words is how one of them goes stale.
       */
      elements: z.array(cardElementSchema).max(24).default([]),
      /**
       * Which preset dressed this card, and whether it has been changed since.
       * Kept so the gallery can say “Custom, from Lower third” rather than light
       * a tile that no longer describes what is on screen.
       */
      preset: cardPresetRefSchema.optional(),
      logoKey: assetKeySchema.optional(),
    }),
    z.object({
      kind: z.literal("video"),
      assetKey: assetKeySchema,
      trimStart: z.number().nonnegative().default(0),
      muted: z.boolean().default(false),
      fit: z.enum(["cover", "contain"]).default("cover"),
    }),
    z.object({
      kind: z.literal("image"),
      /**
       * Absent until one is chosen. The alternative was a sentinel key pointing
       * at a file that was never uploaded, which the renderer could only show as
       * a broken image and the panel could only describe as a picture.
       */
      assetKey: assetKeySchema.optional(),
      fit: z.enum(["cover", "contain"]).default("cover"),
      kenBurns: z.boolean().default(true),
    }),
    z.object({
      kind: z.literal("text"),
      text: z.string().min(1).max(600),
      animation: z.enum(["fade", "rise", "typewriter", "word-by-word"]).default("rise"),
    }),
  ]),
);

/**
 * HEAD and END are configured independently of the BODY: nothing here reads from
 * the recording, so either can be enabled, edited or removed on its own.
 */
/**
 * A HEAD or END scene.
 *
 * Wrapped in a preprocess for the same reason the content is: a card used to
 * carry an `entrance` that every element inherited, and retiring it means
 * moving what it described onto the elements rather than deleting the movement
 * along with the control. See `withElementMotion`.
 */
export const bookendSceneSchema = z.preprocess(
  withElementMotion,
  z.object({
    id: z.string().min(1).max(64),
    type: z.enum(["head", "end"]),
    enabled: z.boolean().default(true),
    duration: z.number().gt(0).max(120),
    content: bookendContentSchema,
    background: backgroundSchema.default(defaultBackground),
    /** Narration text for this section; the voice provider turns it into audio. */
    script: z.string().max(4000).default(""),
    voiceId: z.string().max(120).optional(),
    /**
     * Whether the generated narration is heard.
     *
     * Silencing is not deleting: the audio stays attached, so bringing the voice
     * back is a toggle rather than paying to synthesise it again. Clearing the
     * script instead would throw the words away with it.
     */
    voiceEnabled: z.boolean().default(true),
    /**
     * Seconds to shift this card's narration from where it would otherwise sit.
     *
     * Placement is automatic and waits for the previous voice to finish; this is
     * for the cases where that is not what you wanted. Bounded because the voice
     * is always pulled back inside its own card anyway.
     */
    voiceOffset: z.number().min(-30).max(30).default(0),
  }),
);

export const bodySceneSchema = z.object({
  id: z.string().min(1).max(64),
  type: z.literal("body"),
  enabled: z.literal(true).default(true),
  /** The uploaded recording. BODY always has a visual source. */
  assetKey: assetKeySchema,
  /**
   * A constant-frame-rate copy for the editor preview. The final render always
   * uses `assetKey`, so the proxy costs nothing in quality — it exists because
   * a browser cannot seek a variable-frame-rate screen recording smoothly.
   */
  proxyKey: assetKeySchema.optional(),
  trimStart: z.number().nonnegative().default(0),
  /**
   * The stretches of the recording that play, in order, as source seconds.
   *
   * Empty means the whole thing, which is what every uncut project carries.
   *
   * Cuts used to be made by re-encoding the master into a second file — minutes
   * of ffmpeg on a 4K recording, a second copy on disk, and a decision that
   * could only be undone by transcoding all over again. Expressed here instead
   * they are just numbers: the composition lays one clip per range end to end,
   * so a cut costs nothing to make, nothing to undo and nothing to adjust.
   *
   * These are positions in the *source*. Everything downstream — narration,
   * captions, zooms — is already mapped onto the shortened timeline, so nothing
   * else has to know.
   */
  segments: z
    .array(z.object({ start: z.number().nonnegative(), end: z.number().nonnegative() }))
    .max(500)
    .default([]),
  duration: z.number().gt(0).max(limits.maxVideoDurationSeconds),
  fit: z.enum(["cover", "contain"]).default("contain"),
  /**
   * The recording's own width ÷ height.
   *
   * Only the framed layout needs it, and it needs it badly: without the shape
   * of the footage the card can only be the whole padded area, so a recording
   * that does not share the output's aspect gets letterboxed *inside* the
   * rounded rectangle — the corners then hug empty backdrop rather than the
   * picture, which is the difference between a product shot and a mistake.
   * Optional because timelines built before this existed do not carry it.
   */
  aspect: z.number().positive().max(10).optional(),
  background: backgroundSchema.default(defaultBackground),
  frame: frameSchema.default(defaultFrame),
  // The recording's own audio is not configured here. It is extracted to its
  // own asset and lives in `timeline.audio` as an ordinary track, so the editor
  // shows it as a layer and one setting cannot disagree with another.
  /** Detected or hand-placed interactions, kept for the editor. */
  actions: z.array(actionSchema).max(limits.maxActions).default([]),
  /** The rendered result of those actions. Renderers read this, never `actions`. */
  focus: z.array(focusEffectSchema).max(limits.maxActions).default([]),
  script: z.string().max(limits.maxTranscriptLength).default(""),
  voiceId: z.string().max(120).optional(),
});

export type Background = z.infer<typeof backgroundSchema>;
export type Frame = z.infer<typeof frameSchema>;
export type BookendScene = z.infer<typeof bookendSceneSchema>;
export type BookendContent = z.infer<typeof bookendContentSchema>;
export type BodyScene = z.infer<typeof bodySceneSchema>;
